import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    captchaAnswer: ''
  });

  const [status, setStatus] = useState({
    type: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [captcha, setCaptcha] = useState({
    num1: 0,
    num2: 0,
    operator: '+',
    answer: 0
  });

  useEffect(() => {
    generateNewCaptcha();
  }, []);

  const generateNewCaptcha = () => {
    const operators = ['+', '*'];
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer;
    switch(operator) {
      case '+':
        answer = num1 + num2;
        break;
      case '*':
        answer = num1 * num2;
        break;
      default:
        answer = num1 + num2;
    }

    setCaptcha({ num1, num2, operator, answer });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    if (parseInt(formData.captchaAnswer) !== captcha.answer) {
      setStatus({
        type: 'danger',
        message: 'Incorrect answer. Please try again.'
      });
      generateNewCaptcha();
      setFormData(prev => ({ ...prev, captchaAnswer: '' }));
      setIsSubmitting(false);
      return;
    }

    try {
      // Sanitize message by removing HTML tags
      const sanitizedMessage = formData.message.replace(/<[^>]*>/g, '');
      
      const response = await fetch('/api/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: sanitizedMessage
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Message sent successfully! We will get back to you soon.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          message: '',
          captchaAnswer: ''
        });
        generateNewCaptcha();
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      setStatus({
        type: 'danger',
        message: error.message || 'Failed to send message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <div className="bg-dark min-vh-100">
      <Container className="py-5">
        <h1 className="text-center display-4 fw-bold mb-5 text-light">Contact Us</h1>
        <Row className="g-4">
          <Col md={6}>
            <div className="bg-dark-subtle p-4 rounded shadow">
              <h2 className="text-white mb-4">Get in Touch</h2>
              <p className="text-primary mb-4">
                Have a question or want to learn more about our services? We'd love to hear from you. Fill out the form and we'll get back to you as soon as possible.
              </p>
              <div className="mb-3">
                <i className="bi bi-envelope-fill text-warning me-2"></i>
                <a href="mailto:info@klassi.co.uk" className="text-primary text-decoration-none">
                  info@klassi.co.uk
                </a>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="bg-dark-subtle p-4 rounded shadow">
              <h2 className="text-white mb-4">Send us a Message</h2>
              {status.message && (
                <Alert variant={status.type} className="mb-4">
                  {status.message}
                </Alert>
              )}
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-primary fw-bold">Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name" 
                    className="bg-dark text-primary border-secondary" 
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-primary fw-bold">Email</Form.Label>
                  <Form.Control 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your email" 
                    className="bg-dark text-primary border-secondary" 
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-primary fw-bold">Message</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message" 
                    className="bg-dark text-primary border-secondary" 
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-primary fw-bold">
                    Human Check: What is {captcha.num1} {captcha.operator} {captcha.num2}?
                  </Form.Label>
                  <Form.Control 
                    type="number" 
                    name="captchaAnswer"
                    value={formData.captchaAnswer}
                    onChange={handleChange}
                    placeholder="Enter your answer" 
                    className="bg-dark text-primary border-secondary" 
                    required
                  />
                </Form.Group>
                <Button 
                  variant="warning" 
                  type="submit" 
                  className="fw-bold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Contact; 